import angular from 'angular';

import importDoneUrl from '../manage/users/import-done.html';

const SHALLOW_COPY_PROPS = ['locations', 'groups', 'positions', 'managers'];

export default function PersonaCollection(
  Authentication,
  Restangular,
  EventProcessorService,
  TagsService,
  sdk,
  $rootScope,
  $uibModal,
  $timeout,
  $injector
) {
  'ngInject';

  const service = this;
  const extendUserModel = sdk('model-extensions/extendUserModel');
  const { USER_BASE_WAGE_KEY } = sdk('constants');
  let _store = {};
  let _relationshipCache;
  let _promise = null;
  let _personasDict = null;
  const _onChangeHandlers = [];
  let _initialPersonas = null;

  TagsService.registerOnChangeCallback(_notifyOnChange);

  // GETTERS

  Object.defineProperty(service, 'personas', {
    get() {
      return Array.prototype.concat(
        service.users,
        service.groups,
        service.positions,
        service.locations,
        service.everyones
      );
    },
  });

  Object.defineProperty(service, 'allPersonas', {
    get() {
      return Array.prototype.concat(
        service.allUsers,
        service.groups,
        service.archivedGroups,
        service.positions,
        service.archivedPositions,
        service.locations,
        service.archivedLocations,
        service.everyones
      );
    },
  });

  Object.defineProperty(service, 'everyone', {
    get() {
      return service.everyones.length ? service.everyones[0] : null;
    },
  });

  Object.defineProperty(service, 'personasDict', {
    get() {
      if (_personasDict === null) {
        _personasDict = {};

        if (service.allPersonas) {
          service.allPersonas.forEach((p) => {
            if (p) {
              _personasDict[p.id] = p;
            }
          });
        }
      }

      return _personasDict;
    },
  });

  function getDictForPersonas(personas) {
    if (personas) {
      const dict = {};
      personas.forEach((p) => {
        if (p) {
          dict[p.id] = p;
        }
      });

      return dict;
    }

    return null;
  }

  Object.defineProperty(service, 'me', {
    get() {
      return (Authentication.user && service.personasDict[Authentication.user.id]) || {};
    },
  });

  Object.defineProperty(service, 'canSeeOtherLocations', {
    get() {
      return (
        Authentication.user.isManager ||
        Authentication.user.orgSettings.usersCanSeeUsersOutsideLocation
      );
    },
  });

  Object.defineProperty(service, 'scheduleLocations', {
    get() {
      return service.locations?.filter((g) => !g.hiddenOnSchedule) || [];
    },
  });

  Object.defineProperty(service, 'schedulePositions', {
    get() {
      return service.positions?.filter((g) => !g.hiddenOnSchedule) || [];
    },
  });

  Object.defineProperty(service, 'scheduleGroups', {
    get() {
      return service.groups?.filter((g) => !g.hiddenOnSchedule) || [];
    },
  });

  Object.defineProperty(service, 'scheduleUsers', {
    get() {
      return service.users?.filter((u) => !u.hiddenOnSchedule) || [];
    },
  });

  Object.defineProperty(service, 'visibleLocations', {
    get() {
      return service.canSeeOtherLocations ? service.locations : service.me.locations;
    },
  });

  Object.defineProperty(service, 'tags', {
    get() {
      return TagsService.tags;
    },
  });

  Object.defineProperty(service, 'personaCollectionLoaded', {
    get() {
      return _promise;
    },
  });

  Object.defineProperty(service, 'hasImportedUsers', {
    get() {
      return service.allUsers.some((user) => user.origin);
    },
  });

  // PRIVATE

  function _getGPLArrayForType(group) {
    switch (group.type) {
      case 'group':
        return group.archivedAt ? service.archivedGroups : service.groups;

      case 'position':
        return group.archivedAt ? service.archivedPositions : service.positions;

      case 'location':
        return group.archivedAt ? service.archivedLocations : service.locations;

      case 'everyone':
        return service.everyones;

      default:
        break;
    }
  }

  function _filterUsers() {
    service.users = service.allUsers.filter((u) => u.active);
  }

  function _clearData() {
    service.allUsers = [];
    service.users = [];
    service.groups = [];
    service.archivedGroups = [];
    service.positions = [];
    service.archivedPositions = [];
    service.locations = [];
    service.archivedLocations = [];
    service.everyones = [];

    _store = {};
    _personasDict = null;
    _relationshipCache = {};
  }

  function _loadFromServer() {
    return Restangular.one('users/concise')
      .get({ 'user-fields': 'full' })
      .then(({ groups, users }) => {
        _clearData();

        // Now process the users
        users.forEach((user) => {
          service.updateMemberships(user.id, user.groupIds);
        });

        service.allUsers = users;
        _filterUsers();

        // First process the groups: Restangularize & add to service arrays
        _initialPersonas = getDictForPersonas(service.allUsers); // we only need users for building group relationships
        Object.values(groups).forEach((group) => {
          Restangular.restangularizeElement(null, group, 'groups');

          const gplArray = _getGPLArrayForType(group);
          gplArray.push(group);
        });

        _personasDict = null; // restangularizing users needs groups in personasDict (to properly map wages objects)
        _initialPersonas = getDictForPersonas(service.allPersonas); // we need all personas to build user relationships

        // Now process the users
        users.forEach((user) => {
          Restangular.restangularizeElement(null, user, 'users');
        });

        // Filtering available users depend on restriction according to locations
        service.updateVisibleUsers();
        _initialPersonas = null;
        _personasDict = null;

        return Authentication.userLoaded.then(() => {
          Restangular.restangularizeElement(
            null,
            Authentication.user.orgSettings,
            'account/org/settings'
          );
          return true;
        });
      });
  }

  function _notifyOnChange() {
    _updateEventProcessorWorker();
    _onChangeHandlers.forEach((h) => h());
  }

  function _usersImported(data) {
    console.log('employees imported', data);
    if (service.importProcessingModal) {
      service.importProcessingModal.close();
      delete service.importProcessingModal;
    }

    const groupsToAdd = {};

    if (data.users) {
      data.users.forEach((user) => {
        if (user.groups?.length) {
          user.groups.forEach((group) => {
            if (!(group.id in service.personasDict)) {
              if (!(group.id in groupsToAdd)) {
                groupsToAdd[group.id] = group;
                groupsToAdd[group.id].members = [];
              }

              groupsToAdd[group.id].members.push({ id: user.id });
            }
          });
        }
      });

      data.users.forEach((user) => {
        if (service.personasDict[user.id]) {
          return;
        }

        if (user.groups) {
          // filter out groups that are not in the persona collection yet
          user.groups = user.groups.filter((group) => !groupsToAdd[group.id]);
        }
        service.addUser(user, { noNotify: true });
      });
    }

    // add new groups if there are any after the import
    Object.keys(groupsToAdd).forEach((groupId) => {
      service.addGroup(groupsToAdd[groupId], { noNotify: true });
    });

    _notifyOnChange();

    $uibModal
      .open({
        size: '620',
        templateUrl: importDoneUrl,
        controller($scope) {
          'ngInject';

          $scope.data = data;
        },
      })
      .result.ignoreCancel();
  }

  function _updateEventProcessorWorker() {
    EventProcessorService.updatePersonas(service);
  }

  function _removeWageIfGroupChanged(user, updatedKey, originalId) {
    Object.entries(user.wages).forEach(([wageGroupKey, groupArray]) => {
      const existingWageIndex = groupArray.findIndex((w) => w.id === originalId);
      if (existingWageIndex >= 0 && String(wageGroupKey) !== String(updatedKey)) {
        groupArray.splice(existingWageIndex, 1);
      }
    });
  }

  _clearData();
  // PUBLIC

  service.clearData = function clearData() {
    _promise = null; // by setting _promise to null we can call loadData again
    _clearData();
  };

  service.loadData = async function loadData() {
    if (_promise !== null) {
      return await _promise;
    }

    if (Authentication.user) {
      _promise = _loadFromServer();
      _loadTags();

      try {
        const r = await _promise;
        _updateEventProcessorWorker();

        return r;
      } catch (error) {
        _promise = null;
        throw error;
      }
    }

    return await _promise;
  };

  function _loadTags() {
    TagsService.loadData();
  }

  service.getStore = function getStore() {
    return _store;
  };

  service.updateMemberships = function updateMemberships(personaId, ids, idsAreTypeOf) {
    _relationshipCache = {};

    if (_store[personaId]) {
      Object.keys(_store[personaId]).forEach((id) => {
        delete _store[id][personaId];

        if (idsAreTypeOf && !ids.includes(id)) {
          const persona = service.allPersonas.find((p) => String(p.id) === String(id));
          Restangular.restangularizeElement(null, persona, idsAreTypeOf);
        }
      });
    }

    _store[personaId] = {};

    ids.forEach((id) => {
      if (!_store[id]) {
        _store[id] = {};
      }

      _store[personaId][id] = true;
      _store[id][personaId] = true;
    });

    _relationshipCache = {};

    if (idsAreTypeOf) {
      ids.forEach((id) => {
        const persona = service.allPersonas.find((p) => String(p.id) === String(id));
        Restangular.restangularizeElement(null, persona, idsAreTypeOf);
      });
    }
  };

  service.getRelationships = function getRelationships(personaId) {
    if (_relationshipCache && _relationshipCache[personaId]) {
      return _relationshipCache[personaId];
    }
    if (_store[personaId]) {
      const relevantPersonasDict = _initialPersonas || getDictForPersonas(service.allPersonas);
      const ids = Object.keys(_store[personaId]);

      _relationshipCache[personaId] = ids.map((id) => relevantPersonasDict[id]).filter((p) => p);

      return _relationshipCache[personaId];
    }

    return [];
  };

  service.addUser = function addUser(user, { noNotify = false } = {}) {
    const userInList = service.allUsers.find((u) => u.id === user.id);
    if (!userInList) {
      service.allUsers.push(user);
      _filterUsers();
      _personasDict = null;

      service.updateMemberships(
        user.id,
        user.groups.map((g) => g.id),
        'groups'
      );
      Restangular.restangularizeElement(null, user, 'users');

      if (!noNotify) {
        _notifyOnChange();
      }
    }
  };

  service.editUser = function editUser(user, { noNotify = false } = {}) {
    delete user.password;

    const userInList = service.allUsers.find((u) => u.id === user.id);

    if (!userInList) {
      service.addUser(user, { noNotify });
      return;
    }

    Object.assign(userInList, user);
    _filterUsers();

    if (user.groups) {
      service.updateMemberships(
        user.id,
        user.groups.map((g) => g.id),
        'groups'
      );
    } else {
      service.updateMemberships(user.id, service.personasDict[user.id].groupIds, 'groups');
    }

    Restangular.restangularizeElement(null, userInList, 'users');

    // Update the current user
    if (userInList.isMe) {
      user.org = Authentication.user.org;

      const worldClockChanged =
        userInList.settings &&
        Authentication.user.settings.worldClockEnabled !== userInList.settings.worldClockEnabled;
      const timezoneChanged = Authentication.user.timezone !== userInList.timezone;

      angular.extend(Authentication.user, userInList);
      Restangular.restangularizeElement(
        null,
        Authentication.user.orgSettings,
        'account/org/settings'
      );
      Authentication.changeUser(Authentication.user);

      if (
        worldClockChanged ||
        (Authentication.user.settings.worldClockEnabled && timezoneChanged)
      ) {
        $injector.get('EventCollection').syncEvents();
        $injector.get('EventCollection')._notifyOnChange();
      }
    }

    if (!noNotify) {
      _notifyOnChange();
    }
  };

  service.deleteUser = function deleteUser(user, { noNotify = false } = {}) {
    const userIndex = service.allUsers.findIndex((u) => u.id === user.id);
    if (userIndex >= 0) {
      service.allUsers.splice(userIndex, 1);

      _personasDict = null;

      service.updateMemberships(user.id, [], 'groups');
      _filterUsers();

      if (!noNotify) {
        _notifyOnChange();
      }
    }
  };

  service.addGroup = function addGroup(group, { noNotify = false } = {}) {
    const gplArray = _getGPLArrayForType(group);
    const groupExists = gplArray.find((item) => item.id === group.id);
    if (!groupExists) {
      gplArray.push(group);
      _personasDict = null;

      service.updateMemberships(
        group.id,
        group.members.map((g) => g.id),
        'users'
      );
      Restangular.restangularizeElement(null, group, 'groups');

      if (!noNotify) {
        _notifyOnChange();
      }
    }
  };

  service.editGroup = function editGroup(group, { noNotify = false } = {}) {
    const groupInList = service.allPersonas.find((g) => g.id === group.id);
    if (Boolean(groupInList.archivedAt) !== Boolean(group.archivedAt)) {
      const oldArray = _getGPLArrayForType(groupInList);
      const index = oldArray.indexOf(groupInList);
      oldArray.splice(index, 1);
      _getGPLArrayForType(group).push(groupInList);
    }

    angular.extend(groupInList, group);

    service.updateMemberships(
      group.id,
      group.members.map((g) => g.id),
      'users'
    );
    Restangular.restangularizeElement(null, groupInList, 'groups');

    if (!noNotify) {
      _notifyOnChange();
    }
  };

  service.deleteGroup = function deleteGroup(group, { noNotify = false } = {}) {
    const gplArray = _getGPLArrayForType(group);
    const index = gplArray.findIndex((g) => g.id === group.id);
    if (index > -1) {
      gplArray.splice(index, 1);
      _personasDict = null;

      service.updateMemberships(group.id, [], 'users');

      if (!noNotify) {
        _notifyOnChange();
      }
    }
  };

  service.unwrapUsers = function unwrapUsers(personas, withDeactivated) {
    const users = [];
    personas.forEach((persona) => {
      if (
        persona.type === 'user' &&
        !users.find((member) => member.id === persona.id) &&
        (persona.active || withDeactivated)
      ) {
        users.push(persona);
      } else if (persona.members) {
        persona.members.forEach((m) => {
          if (
            !users.find((member) => member.id === m.id) &&
            service.personasDict[m.id] &&
            (service.personasDict[m.id].active || withDeactivated)
          ) {
            users.push(service.personasDict[m.id]);
          }
        });
      }
    });

    return users;
  };

  service.onChange = function onChange(handler) {
    _onChangeHandlers.push(handler);

    return function deregisterOnChange() {
      const index = _onChangeHandlers.indexOf(handler);
      if (index >= 0) {
        _onChangeHandlers.splice(index, 1);
      }
    };
  };

  service.updateWages = function updateWages(wagesArray) {
    if (!__TEST__) {
      console.log('PersonaCollection.updateWages', wagesArray);
    }

    wagesArray.forEach((wage) => {
      const { id, dateEffective, regularRate, positionId, locationId, userId, isSalary } = wage;
      const wageObject = { id, dateEffective, regularRate, isSalary, locationId };
      let wages;
      let extendAfterMutation = () => {};

      if (userId) {
        const user = service.personasDict[userId];
        if (!user) {
          return;
        }

        if (!positionId) {
          if (!user.wages[USER_BASE_WAGE_KEY]) {
            user.wages[USER_BASE_WAGE_KEY] = [];
          }

          wages = user.wages[USER_BASE_WAGE_KEY];
          _removeWageIfGroupChanged(user, USER_BASE_WAGE_KEY, id);
        } else {
          if (!(positionId in user.wages)) {
            user.wages[positionId] = [];
          }
          wages = user.wages[positionId];
          _removeWageIfGroupChanged(user, positionId, id);
        }

        extendAfterMutation = () => {
          extendUserModel(user);
        };
      } else {
        const position = service.personasDict[positionId];
        if (!position) {
          return;
        }

        wages = position.wages;
        extendAfterMutation = () => {
          Restangular.restangularizeElement(null, position, 'groups');
        };
      }

      if (!wage.deleted) {
        const existingWage = wages.find((w) => w.id === wageObject.id);
        if (existingWage) {
          // edit
          Object.assign(existingWage, wageObject);
        } else {
          // add
          wages.push(wageObject);
        }
      } else {
        // delete
        const wageIndex = wages.findIndex((w) => w.id === wageObject.id);
        if (wageIndex >= 0) {
          wages = wages.splice(wageIndex, 1);
        }
      }

      extendAfterMutation();
    });

    _notifyOnChange();
  };

  service.getDeletedUsers = async function getDeletedUsers(ids) {
    let deletedUsersDict = {};
    const deletedUserIds = ids.filter((userId) => !(userId in service.personasDict));
    if (deletedUserIds.length) {
      const { data: deletedUsers } = await Authentication.sendRequest({
        path: 'users',
        params: { ids: deletedUserIds.join(','), includeDeleted: true },
      });

      deletedUsersDict = deletedUsers.reduce(
        (agg, deletedUser) => (
          (agg[deletedUser.id] = extendUserModel({ ...deletedUser, deleted: true })), agg
        ),
        {}
      );
    }

    return deletedUsersDict;
  };

  service.getUser = async function getUser(userId) {
    const cachedUser = service.personasDict[userId];
    if (cachedUser) {
      return cachedUser;
    }

    // The user is not in the per-session cache yet (e.g. just created/imported and
    // the addUser interceptor did not run, or a socket:user:new event was dropped).
    // Fetch it by id and fold it into the collection so subsequent lookups hit the
    // cache. `user-fields=full` matches _loadFromServer so the fetched object
    // carries the `groups` array that addUser expects. A 404/403 (unknown user or
    // no access) resolves to undefined, letting the route guard fall through to 404.
    try {
      const user = await Restangular.one('users', userId).get({ 'user-fields': 'full' });
      if (!user) {
        return undefined;
      }

      service.addUser(user);
      return service.personasDict[userId];
    } catch (error) {
      return undefined;
    }
  };

  service.hasAccessToUser = function hasAccessToUser(userId) {
    return service.canSeeOtherLocations || service.visibleUsers.some((user) => user.id === userId);
  };

  // Real time socket updates

  $rootScope.$on('socket:user:new', (event, data) => {
    console.log('user:new', data);
    service.addUser(data);
  });

  $rootScope.$on('socket:user:edit', (event, data) => {
    console.log('user:edit', data);
    service.editUser(data);
  });

  $rootScope.$on('socket:user:delete', (event, data) => {
    console.log('user:delete', data);
    service.deleteUser(data);
  });

  $rootScope.$on('socket:group:new', (event, data) => {
    service.addGroup(data);
  });

  $rootScope.$on('socket:group:edit', (event, data) => {
    service.editGroup(data);
  });

  $rootScope.$on('socket:groups:import', (event, data) => {
    data.groups.forEach((group) => {
      if (service.personasDict[group.id]) {
        service.editGroup(group);
      } else {
        service.addGroup(group);
      }
    });
  });

  $rootScope.$on('socket:group:delete', (event, data) => {
    service.deleteGroup(data);
  });

  $rootScope.$on('socket:user:import', (event, data) => {
    // the timeout is to prevent closing the "import scheduled" modal immediately after showing it.
    $timeout(() => {
      _usersImported(data);
    }, 1000);
  });

  $rootScope.$on('socket:wage:new', (event, data) => {
    service.updateWages(data);
  });

  $rootScope.$on('socket:wage:edit', (event, data) => {
    service.updateWages(data);
  });

  $rootScope.$on('socket:wage:delete', (event, data) => {
    service.updateWages(data);
  });

  $rootScope.$on('socket:org:edit', (event, data) => {
    if (service.canSeeOthersCache === data.settings?.usersCanSeeUsersOutsideLocation) {
      return;
    }
    _notifyOnChange();
  });

  // fullname format might have changed
  $rootScope.$on('event:user-settings-changed', (event, worldClockChanged) => {
    if (service.allUsers) {
      Restangular.restangularizeCollection(null, service.allUsers, 'users');
    }

    if (worldClockChanged) {
      $injector.get('EventCollection').syncEvents();
      $injector.get('EventCollection')._notifyOnChange();
    }
  });

  service.refresh = async function refresh() {
    _promise = null;
    TagsService.refresh();

    const response = await service.loadData();
    _notifyOnChange();
    return response;
  };

  service.archiveGroups = async function archiveGroups(groups, params) {
    const response = await Authentication.sendRequest({
      path: 'groups/archive',
      method: 'post',
      params: {
        id: groups.map((g) => g.id),
        ...params,
      },
    });

    response.data.forEach(service.editGroup);
    $injector.get('EventCollection').syncEvents();
  };

  service.unarchiveGroups = async function unarchiveGroups(groups) {
    const response = await Authentication.sendRequest({
      path: 'groups/archive',
      method: 'delete',
      params: {
        id: groups.map((g) => g.id),
      },
    });

    response.data.forEach(service.editGroup);
    $injector.get('EventCollection').syncEvents();
  };

  service.setDirty = function setDirty() {
    _promise = null;
  };

  service.updateVisibleUsers = function updateVisibleUsers() {
    service.canSeeOthersCache = Authentication.user.orgSettings.usersCanSeeUsersOutsideLocation;
    service.visibleUsers = service.canSeeOtherLocations
      ? service.users
      : service.users.filter((user) =>
          user.locationIds?.some((id) => service.me.locationIds.includes(id))
        );
  };

  service.getShallowUserCopy = function getShallowUserCopy(originalUser, copyGroupsFn = null) {
    let userCopy = {};
    Object.keys(originalUser).forEach((key) => {
      if (SHALLOW_COPY_PROPS.includes(key)) {
        userCopy[key] = [];
      } else {
        userCopy[key] = originalUser[key];
      }
    });

    userCopy = Restangular.copy(userCopy);

    const copyFn =
      copyGroupsFn ||
      ((p) => ({ id: p.id, type: p.type, name: p.name, fullname: p.fullname, color: p.color }));
    SHALLOW_COPY_PROPS.forEach((key) => {
      userCopy[key] = originalUser[key].map(copyFn);
    });

    return userCopy;
  };

  $rootScope.pp = service;
}
